import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import { Search, TriangleAlert, Clipboard, SquareCheckBig, Plus, RotateCcw, Inbox, Calendar, User } from 'lucide-react';
import { estadosDisponibles } from '@/config/constants';

const SolicitudesListView = () => {
  const { user } = useAuth();
  const { success, error } = useNotifications();
  const navigate = useNavigate();
  const [solicitudes, setSolicitudes] = useState([]);
  const [solicitudesOriginales, setSolicitudesOriginales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todas');
  const [ordenarPor, setOrdenarPor] = useState('fecha_desc');
  const [busqueda, setBusqueda] = useState('');
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

  useEffect(() => {
    fetchSolicitudes();
  }, []);

  useEffect(() => {
    aplicarFiltros();
  }, [filtroEstado, ordenarPor, busqueda, solicitudesOriginales]);

  const aplicarFiltros = () => {
    let solicitudesFiltradas = [...solicitudesOriginales];

    // Aplicar búsqueda de texto
    if (busqueda) {
      solicitudesFiltradas = solicitudesFiltradas.filter(s =>
        s.tramite_nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
        s.cliente_nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
        s.categoria_nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
        s.gestor_nombre?.toLowerCase().includes(busqueda.toLowerCase())
      );
    }

    // Aplicar filtro de estado
    if (filtroEstado !== 'todas') {
      solicitudesFiltradas = solicitudesFiltradas.filter(s => s.status === filtroEstado);
    }

    // Aplicar ordenamiento
    solicitudesFiltradas.sort((a, b) => {
      switch (ordenarPor) {
        case 'fecha_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'fecha_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'tramite':
          return (a.tramite_nombre || '').localeCompare(b.tramite_nombre || '');
        case 'estado':
          return (a.status || '').localeCompare(b.status || '');
        default:
          return 0;
      }
    });

    setSolicitudes(solicitudesFiltradas);
  };

  const fetchSolicitudes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/applications/enriched`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const solicitudesList = data.solicitudes || [];

        setSolicitudesOriginales(solicitudesList);
      } else {
        error('Error cargando solicitudes. Intenta de nuevo.');

        if (response.status === 401) {
          success('❌ Sesión expirada. Recarga la página e inicia sesión nuevamente.');
        } else if (response.status === 403) {
          success('❌ No tienes permisos para ver solicitudes.');
        } else {
          success('❌ Error cargando solicitudes. Intenta de nuevo.');
        }
      }
    } catch (err) {
      success('❌ Error de conexión. Verifica tu internet e intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSolicitud = (solicitud) => {
    setTimeout(() => {
      navigate(`/applications/${solicitud._id}`);
    }, 500);
  };

  if (loading) {
    return <LoadingSpinner message="Cargando solicitudes..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestión de Solicitudes</h1>
              <p className="text-gray-600 mt-2">
                {user?.role === 'cliente' ? 'Tus solicitudes de trámites' :
                  user?.role === 'gestor' ? 'Solicitudes asignadas a ti' :
                    'Todas las solicitudes del sistema'}
              </p>
            </div>

            <button
              onClick={() => navigate('/categorias')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex row items-center"
            >
              <Plus className='mr-1' /> Nueva Solicitud
            </button>
          </div>
        </div>


        {/* Estadísticas rápidas */}
        {(user.role == "gestor" || user.role == "admin") && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <EstadisticaCard
              title="Total Solicitudes"
              value={solicitudes.length}
              icon={<Clipboard />}
              color="blue"
            />
            <EstadisticaCard
              title="En Proceso"
              value={solicitudes.filter(s => !['completado', 'cancelado'].includes(s.status)).length}
              icon={<RotateCcw />}
              color="yellow"
            />
            <EstadisticaCard
              title="Completadas"
              value={solicitudes.filter(s => s.status === 'completado').length}
              icon={<SquareCheckBig />}
              color="green"
            />
            <EstadisticaCard
              title="Sin Gestor"
              value={solicitudes.filter(s => !s.assigned_gestorid).length}
              icon={<TriangleAlert />}
              color="orange"
            />
          </div>)
        }

        {/* Filtros y controles mejorados */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          {/* Búsqueda principal */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex row items-center">
              Buscar en solicitudes
            </label>
            <div className="relative">
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por trámite, cliente, categoría o gestor..."
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400 text-lg"><Search /></span>
              </div>
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Filtros rápidos */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filtrar por estado
                </label>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {estadosDisponibles.map((estado) => (
                    <option key={estado._id} value={estado._id}>
                      {estado.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordenar por
                </label>
                <select
                  value={ordenarPor}
                  onChange={(e) => setOrdenarPor(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="fecha_desc">Fecha (Reciente)</option>
                  <option value="fecha_asc">Fecha (Antiguo)</option>
                  <option value="tramite">Tipo Trámite</option>
                  <option value="estado">Estado</option>
                </select>
              </div>

              <div>
                <button
                  onClick={() => setMostrarFiltrosAvanzados(!mostrarFiltrosAvanzados)}
                  className="mt-6 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex row items-center"
                >
                  <Inbox className='mr-1' /> {mostrarFiltrosAvanzados ? 'Ocultar Filtros' : 'Más Filtros'}
                </button>
              </div>
            </div>

            <div className="text-sm text-gray-500">
              {solicitudes.length} solicitudes encontradas
            </div>
          </div>
        </div>

        {/* Tabla de solicitudes */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {solicitudes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trámite
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha Solicitud
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gestor
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {solicitudes.map((solicitud) => {
                    const estadoInfo =
                      estadosDisponibles.find(e => e.id === solicitud.status) ||
                      { icon: '❓', color: 'gray', name: solicitud.status };

                    return (
                      <tr
                        key={solicitud._id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleOpenSolicitud(solicitud)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">
                              {solicitud.cliente_nombre}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{solicitud.tramite_nombre}</div>
                          <div className="text-sm text-gray-500">ID: {solicitud._id}...</div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex row items-center gap-1">
                          <Calendar /> {new Date(solicitud.created_at).toLocaleDateString()}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs flex row items-center gap-1 font-medium bg-${estadoInfo.color}-100 text-${estadoInfo.color}-800`}>
                            { estadoInfo.icon } {estadoInfo.name}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {solicitud.assigned_gestor_id ? (
                              <span className="text-green-600 font-medium flex row items-center gap-1">
                                <User /> {solicitud.gestor_nombre}
                              </span>
                            ) : (
                              <span className="text-orange-600 font-medium flex row items-center">
                                <TriangleAlert className='mr-1' /> Sin Asignar
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenSolicitud(solicitud);
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Ver
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center py-16">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay solicitudes
              </h3>
              <p className="text-gray-500 mb-6">
                {filtroEstado === 'todas'
                  ? 'No se han creado solicitudes aún'
                  : `No hay solicitudes en estado "${estadosDisponibles.find(e => e.value === filtroEstado)?.label}"`
                }
              </p>
              <button
                onClick={() => navigate('/categorias')}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex row items-center"
              >
                <Plus className='mr-1' /> Crear Primera Solicitud
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EstadisticaCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200'
  };

  return (
    <div className={`p-6 rounded-2xl border-2 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  );
};

export default SolicitudesListView;